import { DistributionListSchema } from '@/types/dde/distributionList'
import { MonitoringEventsResponseParser } from '@/types/dde/monitoring'
import {
  OpsConsoleCreateOperatorResponseParser,
  OpsConsoleDeleteOperatorResponseParser,
  OpsConsoleOperatorRequestResponseParser
} from '@/types/dde/operator'
import { OpsConsoleSuspendParser } from '@/types/dde/suspend'
import { getQueryParams } from '@/utils/api/getQueryParams'
import { isMigratedToMSFPSport } from '@/utils/licenseUtils'

const DDE_API_BASE_URL = import.meta.env.VITE_DDE_HTTPAPI
const DDE_MSFP_API_BASE_URL = import.meta.env.VITE_DDE_MSFP_MONITORING

// TODO: clear out endpoints here when operator and licenses pages refactor is complete.
export const DDE_ENDPOINTS = {
  Auth: {
    url: `${DDE_API_BASE_URL}/users/authenticationToken`
  },
  ForgotPassword: (email: string) => ({
    url: `${DDE_API_BASE_URL}/users/${encodeURIComponent(email)}/forgottenpassword`
  }),
  Operators: {
    get: {
      url: `${DDE_API_BASE_URL}/operators`,
      parser: OpsConsoleOperatorRequestResponseParser
    },
    post: {
      url: `${DDE_API_BASE_URL}/operators`,
      parser: OpsConsoleCreateOperatorResponseParser
    },
    Operator: {
      delete: (operatorId: string) => ({
        url: `${DDE_API_BASE_URL}/operator/${encodeURIComponent(operatorId)}`,
        parser: OpsConsoleDeleteOperatorResponseParser
      }),
      Licenses: {
        LicenseProperty: {
          Periods: {
            put: (operatorId: string, licenseProperty: string, periodId: string) => ({
              url: `${DDE_API_BASE_URL}/operators/${encodeURIComponent(operatorId)}/licences/${licenseProperty}/periods/${periodId}`
            })
          },
          Suspend: {
            put: (operatorId: string, licenseProperty: string) => ({
              url: `${DDE_API_BASE_URL}/operators/${encodeURIComponent(operatorId)}/licences/${licenseProperty}/suspend`,
              parser: OpsConsoleSuspendParser
            })
          },
          Unsuspend: {
            put: (operatorId: string, licenseProperty: string) => ({
              url: `${DDE_API_BASE_URL}/operators/${encodeURIComponent(operatorId)}/licences/${licenseProperty}/unsuspend`,
              parser: OpsConsoleSuspendParser
            })
          }
        }
      },
      Token: {
        get: (operatorId: string) => ({
          url: `${DDE_API_BASE_URL}/machineusers/${encodeURIComponent(operatorId)}`
        })
      }
    }
  },
  Users: {
    get: {
      url: `${DDE_API_BASE_URL}/users`
    },
    post: {
      url: `${DDE_API_BASE_URL}/users`
    },
    User: {
      delete: (userId: string) => ({
        url: `${DDE_API_BASE_URL}/users/${encodeURIComponent(userId)}`
      })
    }
  },
  Sport: {
    Competitions: {
      get: (sportType: string) => {
        if (isMigratedToMSFPSport(sportType)) {
          return {
            url: `${DDE_API_BASE_URL}/v2/${sportType}/competitions` as const
          }
        }
        return {
          url: `${DDE_API_BASE_URL}/${sportType}/competitions` as const
        }
      }
    },
    Seasons: {
      get: (sportType: string, queryParams?: { [key: string]: string }) => {
        if (isMigratedToMSFPSport(sportType)) {
          return {
            url: queryParams
              ? `${DDE_API_BASE_URL}/v2/${sportType}/seasons?${getQueryParams(queryParams)}`
              : `${DDE_API_BASE_URL}/v2/${sportType}/seasons`
          }
        }
        return {
          url: queryParams
            ? `${DDE_API_BASE_URL}/${sportType}/seasons?${getQueryParams(queryParams)}`
            : `${DDE_API_BASE_URL}/${sportType}/seasons`
        }
      }
    }
  },
  Monitoring: {
    get: (sportType: string) => ({
      url: `${DDE_MSFP_API_BASE_URL}/${sportType}/monitoring`,
      parser: MonitoringEventsResponseParser
    })
  },
  Timeline: {
    Emails: {
      get: {
        url: `${DDE_API_BASE_URL}/timeline/emails`,
        parser: DistributionListSchema
      },
      post: {
        url: `${DDE_API_BASE_URL}/timeline/emails`
      },
      delete: {
        url: `${DDE_API_BASE_URL}/timeline/emails`
      },
      Sports: {
        put: {
          url: `${DDE_API_BASE_URL}/timeline/emails/sports`
        },
        delete: {
          url: `${DDE_API_BASE_URL}/timeline/emails/sports`
        }
      }
    }
  },
  Media: {
    Operators: {
      get: (operatorId: string) => ({
        url: `${DDE_API_BASE_URL}/media/operators/${encodeURIComponent(operatorId)}`
      }),
      put: (operatorId: string, delayValue: string) => ({
        url: `${DDE_API_BASE_URL}/media/operators/${encodeURIComponent(operatorId)}/delay/${delayValue}`
      })
    }
  }
} as const
